<template>
    <v-col v-bind="DASHBOARD_WIDGET_COLUMNS.LARGE">
        <v-card>
            <v-toolbar color="grey-lighten-4" density="compact" flat>
                <template #title>{{ widget.body?.title }}</template>
            </v-toolbar>
            <v-divider />
            <v-card-text>
                <v-row justify="end">
                    <v-col cols="12" lg="3" md="6">
                        <v-menu v-model="menuFrom" :close-on-content-click="false" transition="scale-transition" offset-y>
                            <template #activator="{ props: activatorProps }">
                                <v-text-field
                                    :model-value="filters.ordersPerImposition__gte"
                                    density="compact"
                                    hide-details
                                    label="Od"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    variant="outlined"
                                    v-bind="activatorProps"
                                />
                            </template>
                            <v-date-picker
                                :model-value="filters.ordersPerImposition__gte"
                                first-day-of-week="1"
                                @update:model-value="onDateChange('ordersPerImposition__gte', $event, 'from')"
                            />
                        </v-menu>
                    </v-col>
                    <v-col cols="12" lg="3" md="6">
                        <v-menu v-model="menuTo" :close-on-content-click="false" transition="scale-transition" offset-y>
                            <template #activator="{ props: activatorProps }">
                                <v-text-field
                                    :model-value="filters.ordersPerImposition__lte"
                                    density="compact"
                                    hide-details
                                    label="Do"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    variant="outlined"
                                    v-bind="activatorProps"
                                />
                            </template>
                            <v-date-picker
                                :model-value="filters.ordersPerImposition__lte"
                                first-day-of-week="1"
                                @update:model-value="onDateChange('ordersPerImposition__lte', $event, 'to')"
                            />
                        </v-menu>
                    </v-col>
                </v-row>

                <v-tabs v-model="tab" color="primary">
                    <v-tab :value="0">tabela</v-tab>
                    <v-tab :value="1">wykres</v-tab>
                </v-tabs>
                <v-window v-model="tab">
                    <v-window-item :value="0">
                        <v-card flat>
                            <v-row justify="end">
                                <v-col cols="auto">
                                    <v-btn color="green" prepend-icon="mdi-file-excel" variant="text" @click="exportReport">
                                        {{ t('common.dashboard.buttons.export') }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <v-data-table
                                :headers="headers"
                                :items="dashboardStore.numberOfOrdersPerImposition"
                                density="compact"
                                hide-default-footer
                                item-value="machine"
                            >
                                <template #item.machine="{ item }">
                                    <strong v-if="item.machine === 'total'">OGÓŁEM:</strong>
                                    <template v-else>{{ item.machine }}</template>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-window-item>
                    <v-window-item :value="1">
                        <v-card flat>
                            <v-row justify="end">
                                <v-col cols="auto">
                                    <v-btn color="red" prepend-icon="mdi-file-pdf-box" variant="text" @click="generatePdf">
                                        {{ t('common.dashboard.buttons.generateReport') }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <div class="dashboard-chart-shell">
                                <canvas ref="chartCanvas" />
                            </div>
                        </v-card>
                    </v-window-item>
                </v-window>
            </v-card-text>
        </v-card>
    </v-col>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Tooltip
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { format, subDays } from 'date-fns'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorHandler } from '@/composables/errorHandler'
import { useDashboardStore, useUserStore } from '@/stores'
import type { DashboardPermission, DashboardWidget, NumberOfOrdersPerImpositionRow } from '@/stores/dashboard'
import type { DataTableHeader } from '@/types/dataTable'
import { normalizeDateOnly } from '@/utils/dateUtils'
import { buildQueryString, DASHBOARD_WIDGET_COLUMNS, formatDateForDisplay } from '@/views/Dashboard/dashboardUtils'

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        widget: DashboardWidget
        permissions?: DashboardPermission[]
    }>(),
    { permissions: () => [] }
)

// 3. Constants
const headers: DataTableHeader[] = [
    { title: 'Maszyna', key: 'machine' },
    { title: 'Ilość montaży', key: 'numberOfImpositions' },
    { title: 'Ilość zamówień', key: 'numberOfOrders' },
    { title: 'Sprawność', key: 'efficiency' }
]

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    ChartDataLabels,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Tooltip
)

// 4. Stores & top-level services
const dashboardStore = useDashboardStore()
const userStore = useUserStore()
const { t } = useI18n()

// 5. Reactive state (refs/reactive)
const filters = reactive({
    ordersPerImposition__gte: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    ordersPerImposition__lte: format(new Date(), 'yyyy-MM-dd')
})
const tab = ref(0)
const chartCanvas = ref<HTMLCanvasElement | null>(null)
const menuFrom = ref(false)
const menuTo = ref(false)
let chart: Chart | null = null

// 8. Handlers / methods
function buildWidgetQuery(): string {
    return buildQueryString(filters).replace(/^\?/, '')
}

function buildReportQuery(): string {
    return buildQueryString(filters, (key) =>
        key === 'ordersPerImposition__gte' ? 'createdAt__gte' : key === 'ordersPerImposition__lte' ? 'createdAt__lte' : key
    )
}

async function getDashboard(): Promise<void> {
    try {
        await dashboardStore.getDashboardItem({
            item: props.widget,
            paginate: '',
            query: buildWidgetQuery()
        })
        if (tab.value === 1) await nextTick(updateChart)
    } catch (error) {
        errorHandler(error)
    }
}

function onFilterDatesChange(): void {
    void getDashboard()
}

function onDateChange(
    key: 'ordersPerImposition__gte' | 'ordersPerImposition__lte',
    value: string | Date | null,
    menu: 'from' | 'to'
): void {
    const normalized = normalizeDateOnly(value)
    if (normalized !== null) filters[key] = normalized
    if (menu === 'from') menuFrom.value = false
    else menuTo.value = false
    onFilterDatesChange()
}

function updateChart(): void {
    if (!chartCanvas.value) return

    const chartData = (dashboardStore.numberOfOrdersPerImposition || []).filter(
        (item: NumberOfOrdersPerImpositionRow) => item.machine !== 'total'
    )

    if (!chart) {
        chart = new Chart(chartCanvas.value as HTMLCanvasElement, {
            type: 'line',
            data: { labels: [] as string[], datasets: [] },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'bottom' },
                    datalabels: { display: false }
                },
                layout: { padding: { top: 20 } },
                scales: {
                    y: { ticks: { stepSize: 20 } },
                    y1: {
                        min: 0,
                        max: 100,
                        position: 'right',
                        grid: { display: false },
                        ticks: { stepSize: 10, callback: (value: string | number) => `${value}%` }
                    }
                }
            }
        })
    }

    chart.data.labels = chartData.map((item) => item.machine)
    chart.data.datasets = [
        {
            type: 'bar',
            label: 'Ilość Montaży',
            data: chartData.map((item) => item.numberOfImpositions ?? null),
            backgroundColor: '#4e81bd',
            borderWidth: 0,
            borderRadius: 5
        },
        {
            type: 'bar',
            label: 'Ilość Zamówień',
            data: chartData.map((item) => item.numberOfOrders ?? null),
            backgroundColor: '#c0504d',
            borderWidth: 0,
            borderRadius: 5
        },
        {
            type: 'line',
            label: 'Sprawność',
            data: chartData.map((item) => item.efficiency ?? null),
            borderColor: '#adc778',
            backgroundColor: '#adc778',
            borderWidth: 4,
            fill: false,
            yAxisID: 'y1'
        }
    ]
    chart.update()
}

async function exportReport(): Promise<void> {
    try {
        await dashboardStore.generateNumberOfOrdersPerImpositionReport({
            query: buildReportQuery(),
            userEmail: userStore.userEmail || ''
        })
    } catch (error) {
        errorHandler(error)
    }
}

async function generatePdf(): Promise<void> {
    if (!chartCanvas.value) return
    const canvas = await html2canvas(chartCanvas.value)
    const doc = new jsPDF({ orientation: 'landscape' })
    const title = props.widget.body && typeof props.widget.body.title === 'string' ? props.widget.body.title : ''
    doc.text(title, 10, 10)
    doc.addImage(canvas.toDataURL('image/png'), 'PNG', 20, 25, 240, 120)
    doc.setFontSize(10)
    doc.text(`${t('common.dashboard.labels.generatedAt')}: ${formatDateForDisplay(new Date(), true)}`, 10, 190)
    window.open(doc.output('bloburl'), '_blank')
}

watch(tab, async (value: number) => {
    if (value === 1) {
        await nextTick()
        updateChart()
    }
})

onMounted(() => {
    void getDashboard()
})
</script>

<style scoped>
.dashboard-chart-shell {
    height: 500px;
    position: relative;
    width: 100%;
}
</style>
